<template>
  <div class="chart-wrapper">
    <div v-show="isLoading" class="loader">
      <v-progress-circular indeterminate color="success" />
      <span class="loader-text text--green-darken-1">Loading...</span>
    </div>
    <div class="chart-toolbar">
      <v-menu :close-on-click="true" :close-on-content-click="false" :open-on-hover="true">
        <template #activator="{ on, attrs }">
          <v-btn color="primary" small dark v-bind="attrs" elevation="0" v-on="on">
            {{ $i18n.t("select") | capitalize }}
          </v-btn>
        </template>
        <v-list>
          <v-list-item v-for="(item, index) in series" :key="item.name + index">
            <v-checkbox v-model="item.isShow"></v-checkbox>
            <v-list-item-title>{{ item.name }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-tooltip bottom>
        <template #activator="{ attrs, on }">
          <v-icon v-bind="attrs" v-on="on" @click="refresh"> $refresh </v-icon>
        </template>
        {{ $i18n.t("restoreDefault") | capitalize }}
      </v-tooltip>
    </div>
    <div style="height: calc(100% - 26px)">
      <apex-charts ref="chart" :options="opt" :series="calculatedSeries" height="100%" />
    </div>
  </div>
</template>

<script lang="ts">
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { ApexOptions } from "apexcharts";
import { Component, Prop, PropSync, Vue } from "vue-property-decorator";

@Component
export default class SimpleChart extends Vue {
  @PropSync("options", { required: true })
  _options!: ApexOptions;
  @PropSync("series", { required: true })
  // eslint-disable-next-line
  _series!: any;
  @PropSync("name", { required: true })
  _name!: string;

  @Prop({ default: false })
  isLoading!: boolean;

  get opt() {
    return {
      ...this._options,
      yaxis: {
        ...this._options.yaxis,
        labels: {
          formatter: function (val: number) {
            return val.toFixed(0);
          },
        },
        min: 0,
        max: this.max === Infinity ? 0 : Math.round(this.max) + 2,
      },
    };
  }

  get filteredSeries() {
    // eslint-disable-next-line
    return this._series.filter((x: any) => x.isShow);
  }

  get calculatedSeries() {
    const name = filterCapitalize(i18n.t("all"));
    // eslint-disable-next-line
    const allSeries = this.filteredSeries.filter((x: any) => x.name == name);
    // eslint-disable-next-line
    const otherSeries = this.filteredSeries.filter((x: any) => x.name != name);

    if (allSeries.length == 1) {
      if (otherSeries.length > 0) {
        for (let i = 0; i < otherSeries[0].data.length; i++) {
          let sum = 0;
          for (let y = 0; y < otherSeries.length; y++) {
            sum += otherSeries[y].data[i];
          }
          allSeries[0].data[i] = sum;
        }
      }
    }

    if (allSeries.length == 1 && otherSeries.length == 0) {
      allSeries[0].data = [];
    }
    // eslint-disable-next-line
    return this.filteredSeries.map((x: any) => ({
      data: x.data,
      name: x.name,
      type: x.seriesType === 1 ? "area" : "line",
    }));
  }

  get max() {
    if (this.calculatedSeries.length === 0) return 10;
    // eslint-disable-next-line
    const max = this.filteredSeries.reduce((acc: number, cur: any) => {
      const val = Math.max.apply(null, cur.data);
      return acc > val ? acc : val;
    }, 0);
    return max === 0 ? 1 : max;
  }

  async getChart() {
    const chart = await this.$store.direct.dispatch.statistics.getChart(this._name);
    this.$emit("setChart", chart);
  }

  async refresh() {
    (this.$refs.chart as HTMLFormElement).resetSeries();
    await this.getChart();
  }

  async mounted() {
    await this.getChart();
  }
}
</script>

<style lang="scss" scoped>
.chart-wrapper {
  height: 100%;
  position: relative;
}

.loader {
  position: absolute;
  left: 50%;
  top: 50%;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  z-index: 9999;
}

.loader-text {
  margin-left: 10px;
  color: #4caf50;
}

.v-list-item {
  min-height: 30px;
}

.v-list-item__action {
  padding: 0;
  margin: 5px 5px 5px 0;
}

.chart-toolbar {
  display: flex;
  flex-direction: row;
  gap: 10px;
}
</style>
